import React, { useMemo, useState, useCallback } from 'react'
import {
  Radio,
  TextField,
  Select,
  ComboboxControlled,
} from 'sidecar-components'
import { stateMapping, formatPhoneNumber } from 'sidecar-utilities'

import { useParentBundles } from 'hooks/useParentBundles'

/**
 * @returns {JSX.Element} - Form fields for the member to answer to explain what
 *   was wrong with their search results.
 */
export const ImproveSearchResultsFields = ({
  formik,
  values,
  touched,
  errors,
  handleBlur,
  handleFieldChange,
}) => {
  const {
    provider,
    practiceName,
    providerSpecialty,
    city,
    address,
    unit,
    state,
    zip,
    phone,
    website,
  } = values
  const isTouched = field => touched[field]

  const stateOptions = useMemo(
    () =>
      Object.keys(stateMapping).map(stateAbbr => ({
        label: stateAbbr,
        value: stateAbbr,
      })),
    [],
  )

  const [isSpecialtyOpen, setIsSpecialtyOpen] = useState(false)

  const { parentBundles } = useParentBundles()

  // All parent bundle options
  const allParentBundleOptions = useMemo(
    () =>
      parentBundles?.map(parentBundle => ({
        label: parentBundle.displayName,
        value: parentBundle.displayName,
      })) || [],
    [parentBundles],
  )

  // Filtered options based on input value
  const filteredParentBundleOptions = useMemo(() => {
    if (!providerSpecialty) return allParentBundleOptions

    return allParentBundleOptions.filter(option =>
      option.label.toLowerCase().includes(providerSpecialty.toLowerCase()),
    )
  }, [allParentBundleOptions, providerSpecialty])

  // we need to grab this to manually set the value for the specialty and state.
  // The onChange events for those widgets don't bubble react synthetic events up,
  // just the raw literal values.
  const { setFieldValue } = formik

  // Handlers for ComboboxControlled
  const handleSpecialtyInputChange = useCallback(newValue => {
    if (typeof newValue === 'string') {
      setFieldValue('providerSpecialty', newValue)
    }
  }, [])

  const handleSpecialtySelect = useCallback(
    selectedOption => {
      if (selectedOption?.value) {
        setFieldValue('providerSpecialty', selectedOption.value)
      } else if (typeof selectedOption === 'string') {
        // handling custom values, aka "search for 'custom term'"
        setFieldValue('providerSpecialty', selectedOption)
      } else {
        // Handle clear
        setFieldValue('providerSpecialty', '')
      }
    },
    [setFieldValue],
  )

  const handleSpecialtyClear = useCallback(() => {
    setFieldValue('providerSpecialty', '')
  }, [setFieldValue])

  const handlePhoneChange = e => {
    const formattedPhone = formatPhoneNumber(e.target.value)
    setFieldValue('phone', formattedPhone)
  }

  return (
    <>
      <p>
        If a provider or practice{`\u2019`}s information needs updating, please
        share their details so we can improve our search results.
      </p>
      <Radio
        title={`What\u2019s the issue?`}
        className="mt-6"
        name="issueType" // field name
        onChange={value => {
          setFieldValue('issueType', value)
        }}
        radios={[
          {
            label: `I can\u2019t find my provider / practice`,
            value: "I can't find my provider / practice",
          },
          {
            label: `My provider\u2019s address, name, or phone is wrong`,
            value: "My provider's address, name, or phone is wrong",
          },
        ]}
      />
      <h4>Tell us about your provider</h4>
      <TextField
        name="provider"
        label={`Provider\u2019s full name`}
        className="w-full mb-8"
        value={provider}
        onBlur={handleBlur}
        onChange={handleFieldChange}
        error={isTouched('provider') && errors.provider}
        testId="input_providerFullName"
        textRedacted={true}
      />
      {allParentBundleOptions?.length > 0 && (
        <ComboboxControlled
          wrapperClass="mb-8"
          className="w-full"
          error={isTouched('providerSpecialty') && errors.providerSpecialty}
          label={`Provider\u2019s specialty`}
          name="providerSpecialty"
          value={providerSpecialty}
          selectedValue={filteredParentBundleOptions.find(
            option => option.value === providerSpecialty,
          )}
          items={isSpecialtyOpen ? filteredParentBundleOptions : []}
          open={isSpecialtyOpen}
          setValue={handleSpecialtyInputChange}
          onSelect={handleSpecialtySelect}
          onOpenChange={setIsSpecialtyOpen}
          onChange={handleSpecialtyInputChange}
          onClear={handleSpecialtyClear}
          searchable
          testId="input_providerSpecialty"
          textRedacted={true}
        />
      )}
      <TextField
        name="practiceName"
        label="Practice name"
        className="w-full mb-8"
        value={practiceName}
        onBlur={handleBlur}
        onChange={handleFieldChange}
        error={isTouched('practiceName') && errors.practiceName}
        testId="input_practiceName"
        textRedacted={true}
      />
      {/** Address row */}
      <div className="w-full mb-8 flex gap-2">
        <TextField
          name="address"
          label="Address"
          className="w-2/3"
          value={address}
          onBlur={handleBlur}
          onChange={handleFieldChange}
          error={isTouched('address') && errors.address}
          testId="input_address"
          textRedacted={true}
        />
        <TextField
          name="unit"
          label="Unit"
          className="w-1/3"
          value={unit}
          onBlur={handleBlur}
          onChange={handleFieldChange}
          error={isTouched('unit') && errors.unit}
          testId="input_unit"
          textRedacted={true}
        />
      </div>
      <TextField
        name="city"
        label="City"
        className="w-full mb-8"
        value={city}
        onBlur={handleBlur}
        onChange={handleFieldChange}
        error={isTouched('city') && errors.city}
        testId="input_city"
        textRedacted={true}
      />
      <div className="w-full flex gap-2 mb-8">
        <Select
          label="State"
          name="state"
          wrapperClass="w-1/2"
          onChange={value => {
            setFieldValue('state', value)
          }}
          options={stateOptions}
          placeholder="Select a state"
          value={state}
          testId="input_state"
          textRedacted={true}
        />
        <TextField
          name="zip"
          label="Zip"
          className="w-1/2"
          value={zip}
          onBlur={handleBlur}
          onChange={handleFieldChange}
          error={isTouched('zip') && errors.zip}
          testId="input_zip"
          pattern="[0-9]*"
          inputMode="numeric"
          maxLength={5}
          textRedacted={true}
        />
      </div>
      <TextField
        name="phone"
        label="Phone"
        className="w-full mb-8"
        value={phone}
        onBlur={handleBlur}
        onChange={handlePhoneChange}
        error={isTouched('phone') && errors.phone}
        testId="input_phone"
        icon="phone"
        placeholder="###-###-####"
        textRedacted={true}
      />
      <TextField
        name="website"
        label="Website"
        className="w-full mb-8"
        value={website}
        onBlur={handleBlur}
        onChange={handleFieldChange}
        error={isTouched('website') && errors.website}
        testId="input_website"
        textRedacted={true}
      />
    </>
  )
}
